import { Component } from '@theme/component';

/**
 * Announcement banner custom element with vertical scroll animation.
 * Text enters from the bottom, stays visible, then exits through the top.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Whether a transition is currently in progress.
   * @type {boolean}
   */
  #isTransitioning = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Initialize: show the first slide, hide all others
    this.refs.slides?.forEach((slide, index) => {
      if (index === 0) {
        slide.setAttribute('aria-hidden', 'false');
        slide.removeAttribute('data-state');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('data-state');
      }
    });

    this.play();
  }

  /**
   * Advances to the next slide with vertical scroll animation.
   */
  next() {
    if (this.#isTransitioning) return;

    const slides = this.refs.slides ?? [];
    if (slides.length <= 1) return;

    this.#isTransitioning = true;

    const currentIndex = this.#current;
    let nextIndex = (currentIndex + 1) % slides.length;

    const currentSlide = slides[currentIndex];
    const nextSlide = slides[nextIndex];

    // Prepare the next slide: position it below (enter from bottom)
    nextSlide.setAttribute('data-state', 'enter');
    nextSlide.setAttribute('aria-hidden', 'false');

    // Force reflow so the browser registers the starting position
    nextSlide.getBoundingClientRect();

    // Animate: current slide exits up, next slide enters from bottom
    currentSlide.setAttribute('data-state', 'exit');
    nextSlide.removeAttribute('data-state');

    // After transition completes, clean up
    const onTransitionEnd = () => {
      currentSlide.removeEventListener('transitionend', onTransitionEnd);
      currentSlide.setAttribute('aria-hidden', 'true');
      currentSlide.removeAttribute('data-state');

      this.#current = nextIndex;
      this.#isTransitioning = false;
    };

    currentSlide.addEventListener('transitionend', onTransitionEnd, { once: true });

    // Fallback in case transitionend doesn't fire
    setTimeout(() => {
      if (this.#isTransitioning) {
        onTransitionEnd();
      }
    }, 700);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
